import { supabase } from '../lib/supabase'
import { fetchAllByRange, SUPABASE_IN_CHUNK_SIZE } from '../lib/supabaseFetchAll'
import { comercialGestaoService } from './comercialGestao'
import {
  brokerContactLogsSelect,
  mapHelpdeskBrokerContactLogRow,
  type HelpdeskBrokerContactLogRow,
} from './helpdeskContactLogs'
import type {
  BrokerLookupItem,
  ContactLogsRelatorioFiltros,
  DiarioRelatorioRow,
  DiaryRelatorioFiltros,
  LigacaoRelatorioRow,
  RelatorioPaginatedResult,
  RelatorioPeriodo,
  UsuarioLookupItem,
} from '../types/comercialContatosPortal'

export type {
  BrokerLookupItem,
  ContactLogsRelatorioFiltros,
  DiarioRelatorioRow,
  DiaryRelatorioFiltros,
  LigacaoRelatorioRow,
  RelatorioPaginatedResult,
  RelatorioPeriodo,
  UsuarioLookupItem,
} from '../types/comercialContatosPortal'

export function formatDateYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function brokerLabelFromEmbed(
  broker:
    | { holder_name?: string | null; name?: string | null }
    | { holder_name?: string | null; name?: string | null }[]
    | null
    | undefined
): string {
  const b = Array.isArray(broker) ? broker[0] : broker
  const name = typeof b?.name === 'string' ? b.name.trim() : ''
  return name || '—'
}

function profileNameFromEmbed(
  profile: { name?: string | null } | { name?: string | null }[] | null | undefined
): string {
  const p = Array.isArray(profile) ? profile[0] : profile
  const n = typeof p?.name === 'string' ? p.name.trim() : ''
  return n || 'Usuário'
}

function applyBrokerFilter<T extends { eq: (col: string, val: string) => T; in: (col: string, vals: string[]) => T }>(
  query: T,
  filtros: { broker_id?: string; broker_ids?: string[] }
): T {
  if (filtros.broker_ids?.length) return query.in('broker_id', filtros.broker_ids)
  if (filtros.broker_id) return query.eq('broker_id', filtros.broker_id)
  return query
}

type ContactLogsQuery = {
  gte: (col: string, val: string) => ContactLogsQuery
  lte: (col: string, val: string) => ContactLogsQuery
  eq: (col: string, val: string | boolean) => ContactLogsQuery
  or: (filters: string) => ContactLogsQuery
}

function applyContactLogsRelatorioFiltros<T extends ContactLogsQuery>(
  query: T,
  filtros: ContactLogsRelatorioFiltros
): T {
  let q: T = query
  if (filtros.contact_date_inicio) q = q.gte('contact_date', filtros.contact_date_inicio) as T
  if (filtros.contact_date_fim) q = q.lte('contact_date', filtros.contact_date_fim) as T
  if (filtros.commercial_contact_reason_id) {
    q = q.eq('commercial_contact_reason_id', filtros.commercial_contact_reason_id) as T
  }
  if (filtros.call_direction) q = q.eq('call_direction', filtros.call_direction) as T
  if (filtros.contact_channel) q = q.eq('contact_channel', filtros.contact_channel) as T
  if (filtros.contact_made === true) {
    q = q.or('contact_made.is.null,contact_made.eq.true') as T
  } else if (filtros.contact_made === false) {
    q = q.eq('contact_made', false) as T
  }
  return q
}

async function listBrokerIdsByComercialUserId(comercialUserId: string): Promise<string[]> {
  if (!comercialUserId) return []

  const { data, error } = await fetchAllByRange<{ broker_id: string }>((from, to) =>
    supabase
      .from('broker_comercial_assignments')
      .select('broker_id')
      .eq('comercial_user_id', comercialUserId)
      .range(from, to)
  )

  if (error) {
    console.warn('[comercialContatosPortal] Erro ao listar corretoras do comercial:', error)
    return []
  }

  return Array.from(new Set((data ?? []).map(r => r.broker_id).filter(Boolean)))
}

async function fetchComercialNamesByBrokerIds(brokerIds: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>()
  const unique = Array.from(new Set(brokerIds.filter(Boolean)))
  if (unique.length === 0) return map

  const assignments: { broker_id: string; comercial_user_id: string }[] = []
  for (let i = 0; i < unique.length; i += SUPABASE_IN_CHUNK_SIZE) {
    const chunk = unique.slice(i, i + SUPABASE_IN_CHUNK_SIZE)
    const { data, error } = await supabase
      .from('broker_comercial_assignments')
      .select('broker_id, comercial_user_id')
      .in('broker_id', chunk)
    if (error) {
      console.warn('[comercialContatosPortal] Erro ao buscar assignments:', error.message)
      continue
    }
    assignments.push(...((data ?? []) as { broker_id: string; comercial_user_id: string }[]))
  }

  const comercialIds = Array.from(new Set(assignments.map(a => a.comercial_user_id).filter(Boolean)))
  const namesById = new Map<string, string>()
  for (let i = 0; i < comercialIds.length; i += SUPABASE_IN_CHUNK_SIZE) {
    const chunk = comercialIds.slice(i, i + SUPABASE_IN_CHUNK_SIZE)
    const { data, error } = await supabase.from('profiles').select('id, name').in('id', chunk)
    if (error) {
      console.warn('[comercialContatosPortal] Erro ao buscar nomes de comerciais:', error.message)
      continue
    }
    for (const u of (data ?? []) as { id: string; name: string | null }[]) {
      const name = typeof u.name === 'string' ? u.name.trim() : ''
      if (name) namesById.set(u.id, name)
    }
  }

  for (const a of assignments) {
    const name = namesById.get(a.comercial_user_id)
    if (name) map.set(a.broker_id, name)
  }
  return map
}

/** Embed de corretora + contato; só `name` no contact para não depender de role/job_title. */
const contactLogsRelatorioSelect =
  `${brokerContactLogsSelect},broker:brokers!helpdesk_broker_contact_logs_broker_id_fkey(name,id_scgp),broker_contact:broker_contacts!helpdesk_broker_contact_logs_broker_contact_id_fkey(name)`

const diaryRelatorioSelect =
  'id,body,created_at,created_by,broker_id,broker:brokers!helpdesk_broker_diary_broker_id_fkey(name),creator:profiles!helpdesk_broker_diary_created_by_fkey(name)'

function idScgpFromBrokerEmbed(
  broker:
    | { id_scgp?: number | string | null }
    | { id_scgp?: number | string | null }[]
    | null
    | undefined
): number | null {
  const b = Array.isArray(broker) ? broker[0] : broker
  const raw = b?.id_scgp
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function mapContactLogRelatorioRow(
  row: HelpdeskBrokerContactLogRow & {
    broker?:
      | { holder_name?: string | null; name?: string | null; id_scgp?: number | null }
      | { holder_name?: string | null; name?: string | null; id_scgp?: number | null }[]
      | null
    broker_contact?:
      | { name?: string | null }
      | { name?: string | null }[]
      | null
  },
  comercialNome: string
): LigacaoRelatorioRow {
  const base = mapHelpdeskBrokerContactLogRow(row)
  const bc = Array.isArray(row.broker_contact) ? row.broker_contact[0] : row.broker_contact
  const contatoNome = typeof bc?.name === 'string' && bc.name.trim() ? bc.name.trim() : undefined

  return {
    ...base,
    corretora_nome: brokerLabelFromEmbed(row.broker),
    corretora_id_scgp: idScgpFromBrokerEmbed(row.broker),
    comercial_responsavel_nome: comercialNome,
    contato_nome: contatoNome,
  }
}

async function resolveBrokerIdsForComercialFiltro(filtros: {
  broker_id?: string
  broker_ids?: string[]
  comercial_user_id?: string
}): Promise<{ broker_ids: string[] | null; empty: boolean }> {
  let ids: string[] | null = null
  if (filtros.broker_ids?.length) ids = [...filtros.broker_ids]
  else if (filtros.broker_id) ids = [filtros.broker_id]

  if (!filtros.comercial_user_id) {
    return { broker_ids: ids, empty: false }
  }

  const fromComercial = await listBrokerIdsByComercialUserId(filtros.comercial_user_id)
  if (fromComercial.length === 0) return { broker_ids: [], empty: true }

  if (ids) {
    const set = new Set(fromComercial)
    const intersected = ids.filter(id => set.has(id))
    if (intersected.length === 0) return { broker_ids: [], empty: true }
    return { broker_ids: intersected, empty: false }
  }

  return { broker_ids: fromComercial, empty: false }
}

function getDefaultRelatorioPeriodo(): RelatorioPeriodo {
  const fim = new Date()
  const inicio = new Date()
  inicio.setDate(inicio.getDate() - 30)
  return { dataInicio: formatDateYmd(inicio), dataFim: formatDateYmd(fim) }
}

function validateRelatorioPeriodo(periodo: RelatorioPeriodo): string | null {
  if (!periodo.dataInicio || !periodo.dataFim) {
    return 'Selecione o período completo (data inicial e final).'
  }
  if (periodo.dataInicio > periodo.dataFim) {
    return 'A data inicial não pode ser posterior à data final.'
  }
  return null
}

/** Cache em memória: lista de comerciais é pequena e compartilhável entre abas. */
let comerciaisResponsaveisCache: Promise<UsuarioLookupItem[]> | null = null

async function listComercialResponsaveisForFilter(): Promise<UsuarioLookupItem[]> {
  if (!comerciaisResponsaveisCache) {
    comerciaisResponsaveisCache = (async () => {
      try {
        const users = await comercialGestaoService.listComercialUsers()
        return users
          .map(u => ({ id: u.id, nome: u.name }))
          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
      } catch (error) {
        comerciaisResponsaveisCache = null
        console.warn('[comercialContatosPortal] Erro ao listar comerciais:', error)
        return []
      }
    })()
  }
  return comerciaisResponsaveisCache
}

type RawContactLogRelatorioRow = Parameters<typeof mapContactLogRelatorioRow>[0]

function compareContactLogsDesc(a: RawContactLogRelatorioRow, b: RawContactLogRelatorioRow): number {
  const d = String(b.contact_date).localeCompare(String(a.contact_date))
  if (d !== 0) return d
  const t = String(b.contact_time).localeCompare(String(a.contact_time))
  if (t !== 0) return t
  return String(b.id).localeCompare(String(a.id))
}

/**
 * Carrega todos os contatos filtrados (sem paginação no servidor).
 * A aba Contatos agrupa por corretora e pagina no cliente.
 */
async function fetchAllContactLogsForRelatorio(
  filtros: ContactLogsRelatorioFiltros
): Promise<{
  data: LigacaoRelatorioRow[]
  error?: string
  empty_reason?: RelatorioPaginatedResult<LigacaoRelatorioRow>['empty_reason']
}> {
  const resolved = await resolveBrokerIdsForComercialFiltro(filtros)
  if (resolved.empty) {
    return { data: [], empty_reason: 'comercial_sem_corretoras' }
  }

  const brokerIds = resolved.broker_ids
  const allRaw: RawContactLogRelatorioRow[] = []

  // Se o comercial tem muitas corretoras, `.in()` único estoura o limite do PostgREST.
  if (brokerIds && brokerIds.length > SUPABASE_IN_CHUNK_SIZE) {
    for (let i = 0; i < brokerIds.length; i += SUPABASE_IN_CHUNK_SIZE) {
      const chunk = brokerIds.slice(i, i + SUPABASE_IN_CHUNK_SIZE)
      const { data: chunkData, error: chunkError } = await fetchAllByRange<RawContactLogRelatorioRow>(
        (rangeFrom, rangeTo) => {
          let q = supabase
            .from('helpdesk_broker_contact_logs')
            .select(contactLogsRelatorioSelect)
            .in('broker_id', chunk)
            .order('contact_date', { ascending: false })
            .order('contact_time', { ascending: false })
            .order('id', { ascending: false })
            .range(rangeFrom, rangeTo)
          q = applyContactLogsRelatorioFiltros(q, filtros)
          return q
        }
      )
      if (chunkError) {
        console.warn('[comercialContatosPortal] Erro ao listar contatos (chunk):', chunkError)
        return { data: [], error: chunkError }
      }
      allRaw.push(...(chunkData ?? []))
    }
  } else {
    const brokerFiltro =
      brokerIds == null
        ? {}
        : brokerIds.length === 1
          ? { broker_id: brokerIds[0] }
          : { broker_ids: brokerIds }

    const { data, error } = await fetchAllByRange<RawContactLogRelatorioRow>((rangeFrom, rangeTo) => {
      let q = supabase
        .from('helpdesk_broker_contact_logs')
        .select(contactLogsRelatorioSelect)
        .order('contact_date', { ascending: false })
        .order('contact_time', { ascending: false })
        .order('id', { ascending: false })
        .range(rangeFrom, rangeTo)
      if (brokerFiltro.broker_id || brokerFiltro.broker_ids?.length) {
        q = applyBrokerFilter(q, brokerFiltro)
      }
      q = applyContactLogsRelatorioFiltros(q, filtros)
      return q
    })

    if (error) {
      console.warn('[comercialContatosPortal] Erro ao listar contatos:', error)
      return { data: [], error }
    }
    allRaw.push(...(data ?? []))
  }

  allRaw.sort(compareContactLogsDesc)

  const comercialNames = await fetchComercialNamesByBrokerIds(
    allRaw.map(r => r.broker_id).filter(Boolean) as string[]
  )

  return {
    data: allRaw.map(row =>
      mapContactLogRelatorioRow(row, comercialNames.get(row.broker_id) || '—')
    ),
  }
}

async function listContactLogsForRelatorio(
  filtros: ContactLogsRelatorioFiltros,
  page = 1,
  pageSize = 20
): Promise<RelatorioPaginatedResult<LigacaoRelatorioRow>> {
  const safePage = Math.max(1, page)
  const safePageSize = Math.max(1, pageSize)

  const resultado = await fetchAllContactLogsForRelatorio(filtros)
  if (resultado.error) {
    return { data: [], total: 0, page: safePage, pageSize: safePageSize, error: resultado.error }
  }
  if (resultado.empty_reason) {
    return {
      data: [],
      total: 0,
      page: safePage,
      pageSize: safePageSize,
      empty_reason: resultado.empty_reason,
    }
  }

  // Retorna o conjunto completo; a UI agrupa por corretora e pagina localmente.
  return {
    data: resultado.data,
    total: resultado.data.length,
    page: 1,
    pageSize: resultado.data.length || safePageSize,
  }
}

async function listDiaryEntriesForRelatorio(
  filtros: DiaryRelatorioFiltros,
  page = 1,
  pageSize = 20
): Promise<RelatorioPaginatedResult<DiarioRelatorioRow>> {
  const safePage = Math.max(1, page)
  const safePageSize = Math.max(1, pageSize)
  const from = (safePage - 1) * safePageSize
  const to = from + safePageSize - 1

  const resolved = await resolveBrokerIdsForComercialFiltro(filtros)
  if (resolved.empty) {
    return { data: [], total: 0, page: safePage, pageSize: safePageSize }
  }

  const brokerFiltro =
    resolved.broker_ids == null
      ? {}
      : resolved.broker_ids.length === 1
        ? { broker_id: resolved.broker_ids[0] }
        : { broker_ids: resolved.broker_ids }

  let query = supabase
    .from('helpdesk_broker_diary')
    .select(diaryRelatorioSelect, { count: 'exact' })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, to)

  if (brokerFiltro.broker_id || brokerFiltro.broker_ids?.length) {
    query = applyBrokerFilter(query, brokerFiltro)
  }
  if (filtros.data_inicio) query = query.gte('created_at', `${filtros.data_inicio}T00:00:00`)
  if (filtros.data_fim) query = query.lte('created_at', `${filtros.data_fim}T23:59:59.999`)

  const { data, error, count } = await query

  if (error) {
    console.warn('[comercialContatosPortal] Erro ao listar diário:', error.message)
    return { data: [], total: 0, page: safePage, pageSize: safePageSize, error: error.message }
  }

  const rows = (data ?? []) as Array<{
    id: string
    body: string
    created_at: string
    created_by: string
    broker_id: string
    broker?:
      | { holder_name?: string | null; name?: string | null }
      | { holder_name?: string | null; name?: string | null }[]
      | null
    creator?: { name?: string | null } | { name?: string | null }[] | null
  }>

  const comercialNames = await fetchComercialNamesByBrokerIds(rows.map(r => r.broker_id))

  return {
    data: rows.map(row => ({
      id: row.id,
      broker_id: row.broker_id,
      body: row.body,
      created_at: row.created_at,
      created_by: row.created_by,
      responsavel_nome: profileNameFromEmbed(row.creator),
      corretora_nome: brokerLabelFromEmbed(row.broker),
      comercial_responsavel_nome: comercialNames.get(row.broker_id) || '—',
    })),
    total: count ?? 0,
    page: safePage,
    pageSize: safePageSize,
  }
}

export const comercialContatosPortalService = {
  getDefaultRelatorioPeriodo,
  validateRelatorioPeriodo,
  resolveBrokerIdsForComercialFiltro,
  listComercialResponsaveisForFilter,
  listContactLogsForRelatorio,
  listDiaryEntriesForRelatorio,
}
